import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity, Briefcase, DollarSign, Users } from 'lucide-react';
import { getSystemOverview, type JobStats, type SystemOverview } from '../api/client';
import { useDebugMode } from '../hooks/useDebugMode';
import { useError } from '../context/ErrorContext';
import { PANEL_BASE } from '../config/paths';
import { SkeletonBar } from './Skeleton';

const JOB_CARDS: {
  key: keyof JobStats;
  labelKey: string;
  valueClass: string;
  boxClass: string;
  labelClass: string;
}[] = [
  {
    key: 'total',
    labelKey: 'panel.totalJobs',
    valueClass: 'text-gray-900 dark:text-white',
    boxClass: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
    labelClass: 'text-gray-500 dark:text-gray-400',
  },
  {
    key: 'pending',
    labelKey: 'panel.statusPending',
    valueClass: 'text-amber-700 dark:text-amber-400',
    boxClass: 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10',
    labelClass: 'text-amber-600 dark:text-amber-500',
  },
  {
    key: 'processing',
    labelKey: 'panel.statusProcessing',
    valueClass: 'text-blue-700 dark:text-blue-400',
    boxClass: 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10',
    labelClass: 'text-blue-600 dark:text-blue-500',
  },
  {
    key: 'completed',
    labelKey: 'panel.statusCompleted',
    valueClass: 'text-green-700 dark:text-green-400',
    boxClass: 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10',
    labelClass: 'text-green-600 dark:text-green-500',
  },
  {
    key: 'failed',
    labelKey: 'panel.statusFailed',
    valueClass: 'text-red-700 dark:text-red-400',
    boxClass: 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10',
    labelClass: 'text-red-600 dark:text-red-500',
  },
  {
    key: 'paused',
    labelKey: 'panel.statusPaused',
    valueClass: 'text-gray-700 dark:text-gray-300',
    boxClass: 'border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50',
    labelClass: 'text-gray-500 dark:text-gray-400',
  },
];

function formatUsd(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

const SystemLoadPage: React.FC = () => {
  const { t } = useTranslation();
  const hasDebugMode = useDebugMode();
  const { showError: showErrorPopup } = useError();
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSystemOverview();
      setOverview(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    if (hasDebugMode) load();
  }, [hasDebugMode, load]);

  if (!hasDebugMode) {
    return <Navigate to={PANEL_BASE} replace />;
  }

  const memberships = overview?.memberships;
  const revenue = overview?.revenue;
  const jobs = overview?.jobs ?? null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="h-7 w-7 text-primary-600" />
            {t('panel.systemOverviewTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('panel.systemOverviewSubtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="self-start px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {t('common.refresh')}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('panel.systemOverviewMembershipsSection')}
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {loading && !memberships
            ? Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                >
                  <SkeletonBar className="h-8 w-12 mb-2" />
                  <SkeletonBar className="h-3 w-16" />
                </div>
              ))
            : (
              <>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {memberships?.total ?? '—'}
                  </div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
                    {t('panel.systemOverviewTotalMembers')}
                  </div>
                </div>
                {(memberships?.byPlan ?? []).map(p => (
                  <div
                    key={p.planId}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                  >
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{p.count}</div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
                      {p.name}
                    </div>
                    {p.monthlyListPriceUsd > 0 && (
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        {formatUsd(p.monthlyListPriceUsd)}
                        {t('panel.systemOverviewPerMonth')}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('panel.systemOverviewRevenueSection')}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {loading && !revenue ? (
            <>
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-4">
                <SkeletonBar className="h-8 w-24 mb-2" />
                <SkeletonBar className="h-3 w-32" />
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <SkeletonBar className="h-8 w-16 mb-2" />
                <SkeletonBar className="h-3 w-28" />
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-4">
                <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                  {revenue ? formatUsd(revenue.monthlyActiveUsd) : '—'}
                </div>
                <div className="text-xs font-medium text-emerald-600 dark:text-emerald-500 uppercase tracking-wider mt-0.5">
                  {t('panel.systemOverviewMonthlyRevenue')}
                </div>
                <p className="text-[11px] text-emerald-700/70 dark:text-emerald-400/70 mt-1">
                  {t('panel.systemOverviewMonthlyRevenueHint')}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {revenue?.activePaying ?? '—'}
                </div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
                  {t('panel.systemOverviewActivePaying')}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('panel.systemOverviewJobsSection')}
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {loading && !jobs
            ? Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                >
                  <SkeletonBar className="h-8 w-16 mb-2" />
                  <SkeletonBar className="h-3 w-20" />
                </div>
              ))
            : JOB_CARDS.map(card => (
                <div key={card.key} className={`rounded-xl border p-4 ${card.boxClass}`}>
                  <div className={`text-2xl font-bold ${card.valueClass}`}>
                    {jobs ? jobs[card.key] : '—'}
                  </div>
                  <div className={`text-xs font-medium uppercase tracking-wider mt-0.5 ${card.labelClass}`}>
                    {t(card.labelKey)}
                  </div>
                </div>
              ))}
        </div>
      </section>
    </div>
  );
};

export default SystemLoadPage;
